import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileImage, X, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api, getToken } from "../api/client";

const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT_TYPES = ["image/jpeg", "image/png", "image/webp"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const validateFile = (file) => {
    if (!file) return "Please select a file";
    if (!ACCEPT_TYPES.includes(file.type)) {
        if (file.type === "application/pdf") return "PDF support is coming soon — please upload an image (JPG/PNG/WEBP).";
        return "Unsupported file type. Please upload JPG, PNG, or WEBP.";
    }
    if (file.size > MAX_BYTES) return `File too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Max 8MB.`;
    return null;
};

const UploadContent = () => {
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [phase, setPhase] = useState("idle"); // idle | uploading | processing | extracted | failed
    const [prescriptionId, setPrescriptionId] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const pickFile = (f) => {
        const err = validateFile(f);
        if (err) {
            toast.error(err);
            return;
        }
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(f);
        setPreviewUrl(URL.createObjectURL(f));
        setErrorMessage(null);
    };

    const onChange = (e) => pickFile(e.target.files?.[0]);

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        pickFile(e.dataTransfer.files?.[0]);
    };

    const reset = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(null);
        setPreviewUrl(null);
        setPhase("idle");
        setPrescriptionId(null);
        setErrorMessage(null);
    };

    const pollExtraction = async (id) => {
        const start = Date.now();
        const MAX_WAIT_MS = 60_000;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            await new Promise((r) => setTimeout(r, 2000));
            try {
                const { data } = await api.get(`/prescriptions/${id}`);
                if (data.ocr_status === "extracted") {
                    setPhase("extracted");
                    return data;
                }
                if (data.ocr_status === "failed") {
                    setPhase("failed");
                    setErrorMessage(data.ocr_error || "Extraction failed. Try a clearer image.");
                    return data;
                }
            } catch {
                // keep polling
            }
            if (Date.now() - start > MAX_WAIT_MS) {
                setPhase("failed");
                setErrorMessage("Extraction is taking longer than expected. Please check back later.");
                return null;
            }
        }
    };

    const onSubmit = async () => {
        if (!file) return;
        setPhase("uploading");
        setErrorMessage(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await fetch(`${BACKEND_URL}/api/prescriptions/upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${getToken()}` },
                body: fd,
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                const detail = data.detail;
                const msg = typeof detail === "string" ? detail : detail?.message || "Upload failed";
                throw new Error(msg);
            }
            const created = await r.json();
            setPrescriptionId(created.id);
            setPhase("processing");
            await pollExtraction(created.id);
        } catch (err) {
            setPhase("failed");
            setErrorMessage(err.message || "Upload failed");
            toast.error(err.message || "Upload failed");
        }
    };

    return (
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]" data-testid="upload-page">
            <div>
                {!previewUrl ? (
                    <div
                        onClick={() => inputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                        className={`flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-zinc-50 p-10 text-center transition-all ${dragging ? "border-brand-500 bg-brand-50" : "border-zinc-200 hover:border-brand-300 hover:bg-brand-50/40"}`}
                        data-testid="upload-dropzone"
                    >
                        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-soft">
                            <Upload className="h-6 w-6 text-brand-700" />
                        </div>
                        <p className="text-base font-medium text-zinc-900">Drop prescription image here</p>
                        <p className="mt-1 text-sm text-zinc-500">or click to choose — JPG, PNG or WEBP, up to 8MB</p>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={onChange}
                            className="hidden"
                            data-testid="upload-file-input"
                        />
                    </div>
                ) : (
                    <div className="relative overflow-hidden rounded-3xl border border-zinc-100 bg-white p-2 shadow-soft" data-testid="upload-preview">
                        <img src={previewUrl} alt="Preview" className="h-[440px] w-full rounded-2xl object-contain bg-zinc-50" />
                        <button
                            onClick={reset}
                            disabled={phase === "uploading" || phase === "processing"}
                            className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-zinc-700 shadow hover:bg-white disabled:opacity-50"
                            aria-label="Remove"
                            data-testid="upload-remove-btn"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </div>

            <aside className="self-start rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="upload-side-panel">
                <p className="label-uppercase">Status</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight">{phase === "idle" ? "Ready to upload" : phase === "uploading" ? "Uploading…" : phase === "processing" ? "Extracting with AI…" : phase === "extracted" ? "Extraction complete" : "Something went wrong"}</h2>

                {file && (
                    <div className="mt-4 flex items-center gap-3 rounded-xl border border-zinc-100 bg-zinc-50/60 p-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white">
                            <FileImage className="h-5 w-5 text-brand-700" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{file.name}</p>
                            <p className="text-xs text-zinc-500">{(file.size / 1024).toFixed(0)} KB</p>
                        </div>
                    </div>
                )}

                {phase === "processing" && (
                    <div className="mt-5 flex items-center gap-2 text-sm text-zinc-600">
                        <Loader2 className="h-4 w-4 animate-spin text-brand-700" />
                        <span>Gemini 2.5 Flash is reading your prescription…</span>
                    </div>
                )}

                {phase === "extracted" && (
                    <div className="mt-5 flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="upload-success">
                        <ShieldCheck className="h-4 w-4" />
                        <span>Extraction complete. Verification is pending.</span>
                    </div>
                )}

                {phase === "failed" && (
                    <div className="mt-5 flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700" data-testid="upload-error">
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>{errorMessage || "Extraction failed."}</span>
                    </div>
                )}

                <div className="mt-6 flex flex-col gap-2">
                    {phase === "idle" || phase === "failed" ? (
                        <button
                            onClick={onSubmit}
                            disabled={!file}
                            className="btn-brand"
                            data-testid="upload-submit-btn"
                        >
                            <Upload className="h-4 w-4" /> Upload & Extract
                        </button>
                    ) : phase === "extracted" ? (
                        <>
                            <button onClick={() => navigate(`/prescriptions/${prescriptionId}`)} className="btn-brand" data-testid="upload-view-detail-btn">
                                View details
                            </button>
                            <button onClick={reset} className="rounded-full border border-zinc-200 px-5 py-2.5 text-sm font-medium hover:border-brand-300">
                                Upload another
                            </button>
                        </>
                    ) : (
                        <button disabled className="btn-brand opacity-70">
                            <Loader2 className="h-4 w-4 animate-spin" /> Working…
                        </button>
                    )}
                </div>

                <ul className="mt-6 space-y-1 text-xs text-zinc-500">
                    <li>• Accepts JPG, PNG, WEBP up to 8MB</li>
                    <li>• Bright, well-lit images work best</li>
                    <li>• Avoid glare and shadows</li>
                </ul>
            </aside>
        </div>
    );
};

const PrescriptionUpload = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <header className="mb-8">
                <p className="label-uppercase">Upload</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">New prescription</h1>
            </header>
            <AuthGuard>
                <UploadContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default PrescriptionUpload;
